<template>
  <div id="app" class="bg-gray-50 min-h-screen">
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-r from-blue-900 to-blue-600 text-white py-16 md:py-24 overflow-hidden">
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-0 left-0 w-64 h-64 bg-blue-400 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div class="absolute bottom-0 right-0 w-96 h-96 bg-blue-300 rounded-full translate-x-1/3 translate-y-1/3"></div>
      </div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="max-w-3xl mx-auto text-center">
          <h1 class="text-4xl md:text-6xl font-bold mb-6 leading-tight">辅具产品</h1>
          <p class="text-xl md:text-2xl mb-8 opacity-90">专业医疗器械，关爱健康生活</p>
          <div class="flex flex-wrap justify-center gap-4">
            <button @click="handleConsult" class="px-8 py-3 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg">
              立即咨询
            </button>
            <button @click="scrollToProducts" class="px-8 py-3 bg-transparent border-2 border-white hover:bg-white hover:text-blue-900 text-white font-semibold rounded-lg transition-all duration-300">
              了解更多
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Categories -->
    <section ref="productSection" class="py-12 md:py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <!-- 辅具产品卡片 -->
          <div class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="p-6">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-blue-900">辅具产品</h3>
              </div>
              <div class="space-y-2">
                <button v-for="item in ['轮椅系列', '坐便器系列', '助行器系列', '多功能系列', '浴室系列']" 
                        :key="item"
                        @click="selectCategory('辅具产品', item)"
                        :class="[
                          'w-full text-left px-4 py-2 rounded-lg transition-colors',
                          selectedCategory === item 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        ]">
                  {{ item }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- 适老化改造产品卡片 -->
          <div class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="p-6">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-green-700">适老化改造产品</h3>
              </div>
              <div class="space-y-2">
                <button v-for="item in ['家居', '扶手', '厕所']" 
                        :key="item"
                        @click="selectCategory('适老化改造产品', item)"
                        :class="[
                          'w-full text-left px-4 py-2 rounded-lg transition-colors',
                          selectedCategory === item 
                            ? 'bg-green-100 text-green-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        ]">
                  {{ item }}
                </button>
              </div>
            </div>
          </div>
          
          <!-- 智能化设备卡片 -->
          <div class="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="p-6">
              <div class="flex items-center mb-4">
                <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"/>
                  </svg>
                </div>
                <h3 class="text-xl font-bold text-purple-700">智能化设备</h3>
              </div>
              <div class="space-y-2">
                <button v-for="item in ['智能产品系列', '智能定位']" 
                        :key="item"
                        @click="selectCategory('智能化设备', item)"
                        :class="[
                          'w-full text-left px-4 py-2 rounded-lg transition-colors',
                          selectedCategory === item 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        ]">
                  {{ item }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Grid -->
    <section class="py-12 md:py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">热门产品</h2>
          <p class="text-gray-600 max-w-2xl mx-auto">精选优质辅具产品，为您的健康生活提供专业保障</p>
        </div>
        
        <div class="mb-8 flex flex-wrap justify-center gap-2">
          <button v-for="category in ['所有', '轮椅系列', '坐便器系列']" 
                  :key="category"
                  @click="selectedCategory = category"
                  :class="[
                    'px-4 py-2 rounded-full transition-all duration-300',
                    selectedCategory === category
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  ]">
            {{ category }}
          </button>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <div v-for="product in filteredProducts" 
               :key="product.id" 
               class="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group border border-gray-200 hover:border-blue-300"
               @click="viewProductDetail(product)">
            <div class="relative overflow-hidden">
              <img :src="product.image" 
                   :alt="product.name" 
                   class="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500">
              <div class="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                热门
              </div>
            </div>
            <div class="p-4">
              <h3 class="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                {{ product.name }}
              </h3>
              <p class="text-gray-600 text-sm mb-4 line-clamp-2">{{ product.description }}</p>
              <div class="flex items-center justify-between">
                <!-- <span class="text-2xl font-bold text-blue-600">¥{{ product.price }}</span> -->
                <button @click.stop="viewProductDetail(product)" 
                        class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-300 flex items-center">
                  <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                  查看详情
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Detail Modal -->
    <div v-if="selectedProduct" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" @click.self="closeModal">
      <div class="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div class="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <h3 class="text-2xl font-bold text-gray-800">{{ selectedProduct.name }}</h3>
          <button @click="closeModal" class="text-gray-500 hover:text-gray-700 transition-colors">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <img :src="selectedProduct.image" 
                   :alt="selectedProduct.name" 
                   class="w-full rounded-xl shadow-lg">
            </div>
            <div>
              <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-700 mb-2">产品描述</h4>
                <p class="text-gray-600 leading-relaxed">{{ selectedProduct.description }}</p>
              </div>
              
              <!-- <div class="mb-6">
                <h4 class="text-lg font-semibold text-gray-700 mb-2">价格</h4>
                <p class="text-4xl font-bold text-blue-600">¥{{ selectedProduct.price }}</p>
                <p class="text-sm text-gray-500 mt-1">市场统一零售价</p>
              </div> -->
              
              <div class="space-y-4">
                <button @click="handleConsult" class="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-300 flex items-center justify-center">
                  <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                  立即咨询购买
                </button>
                <button @click="closeModal" class="w-full py-3 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg transition-colors duration-300">
                  关闭窗口
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'

// 响应式数据
const mobileMenuOpen = ref(false)
const mobileSubMenus = ref({
  about: false,
  products: false
})
const selectedCategory = ref('所有')
const selectedProduct = ref(null)
const productSection = ref(null)

// 产品数据
const products = ref([
  // {
  //   id: 148,
  //   name: '手动轮椅（金属踏板）',
  //   description: '高品质手动轮椅，配备金属踏板，提供舒适耐用的使用体验。采用人体工学设计，轻量化铝合金材质，承重能力强，适合日常使用。',
  //   price: '1280',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0808/20240808085722318.jpg',
  //   category: '轮椅系列'
  // },
  // {
  //   id: 147,
  //   name: '手动轮椅',
  //   description: '经典手动轮椅设计，操作简便，安全可靠。折叠式设计便于携带和存放，适合家庭和户外使用。',
  //   price: '980',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0808/20240808085707983.jpg',
  //   category: '轮椅系列'
  // },
  // {
  //   id: 70,
  //   name: 'YC7813A铝合金座便椅(多功能）',
  //   description: '多功能铝合金座便椅，轻便实用，易于清洁。可调节高度设计，防滑扶手，适用于不同使用环境。',
  //   price: '680',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0801/20240801111344773.png',
  //   category: '坐便器系列'
  // },
  // {
  //   id: 69,
  //   name: 'S500-6电动轮椅',
  //   description: '智能电动轮椅，动力强劲，续航持久。配备多功能控制器，操作简单方便，适合各种路面条件。',
  //   price: '3580',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0719/20240719111738479.png',
  //   category: '轮椅系列'
  // },
  // {
  //   id: 68,
  //   name: 'WY-T01电动轮椅',
  //   description: '高端电动轮椅，智能控制，舒适体验。采用先进电机技术，静音运行，安全可靠。',
  //   price: '4280',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0808/20240808085304874.jpg',
  //   category: '轮椅系列'
  // },
  // {
  //   id: 44,
  //   name: 'YC7808铝合金座便椅 (多功能）',
  //   description: '优质铝合金材质，多功能设计，提供人性化使用体验。易于清洁和维护，使用寿命长。',
  //   price: '720',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0719/20240719105431854.jpg',
  //   category: '坐便器系列'
  // },
  {
    id: 41,
    name: 'FST2000W手动轮椅车',
    description: '专业手动轮椅车，轻量化设计，便于携带。采用高强度材料制造，承重性能优越。',
    price: '1580',
    image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0719/20240719110130820.png',
    category: '轮椅系列'
  },
  // {
  //   id: 40,
  //   name: 'FST3000HGSLJ手动轮椅车',
  //   description: '高端手动轮椅车，精工制造，品质保证。舒适座椅设计，适合长时间使用。',
  //   price: '2180',
  //   image: 'http://zhidakang.cdajcx.com/uploadfile/2024/0719/20240719105754413.png',
  //   category: '轮椅系列'
  // }
])

// 计算属性：筛选产品
const filteredProducts = computed(() => {
  if (selectedCategory.value === '所有') {
    return products.value
  }
  return products.value.filter(product => product.category === selectedCategory.value)
})

// 方法
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value
}

const toggleSubMenu = (menu) => {
  mobileSubMenus.value[menu] = !mobileSubMenus.value[menu]
}

const handleConsult = () => {
  alert('请拨打咨询电话：15928090887\n联系人：林先生')
}

const scrollToProducts = () => {
  productSection.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'start'
  })
}

const selectCategory = (mainCategory, subCategory) => {
  selectedCategory.value = subCategory
  mobileMenuOpen.value = false
  scrollToProducts()
}

const viewProductDetail = (product) => {
  selectedProduct.value = product
  document.body.style.overflow = 'hidden'
}

const closeModal = () => {
  selectedProduct.value = null
  document.body.style.overflow = 'auto'
}

// 生命周期
onMounted(() => {
  // 初始化代码
})
</script>

<style scoped>
/* 自定义样式 */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.nav-link {
  position: relative;
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: -4px;
  left: 0;
  background-color: #3b82f6;
  transition: width 0.3s ease;
}

.nav-link:hover::after {
  width: 100%;
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>